import { useMemo, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { AnimatePresence, motion } from "framer-motion";
import { api } from "@convex/_generated/api";
import type { Doc, Id } from "@convex/_generated/dataModel";
import {
  WEEKDAY_NAMES,
  formatMinute,
  parseMinute,
} from "@convex/lib/time";
import {
  cardClass,
  errorMessage,
  inputClass,
  primaryButtonClass,
  secondaryButtonClass,
} from "@/lib/ui";

type Parity = "all" | "odd" | "even";

type Draft = {
  subjectId: string;
  weekday: number;
  start: string;
  end: string;
  effectiveFrom: string;
  effectiveUntil: string;
  weekParity: Parity;
  location: string;
  category: string;
};

const PARITY_LABEL: Record<Parity, string> = {
  all: "Every week",
  odd: "Odd weeks",
  even: "Even weeks",
};

function todayIso(): string {
  const now = new Date();
  return [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, "0"),
    String(now.getDate()).padStart(2, "0"),
  ].join("-");
}

/** A semester-ish default so the date fields are rarely edited by hand. */
function defaultUntil(): string {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth() + 5, now.getDate());
  return [
    end.getFullYear(),
    String(end.getMonth() + 1).padStart(2, "0"),
    String(end.getDate()).padStart(2, "0"),
  ].join("-");
}

function emptyDraft(subjectId: string): Draft {
  return {
    subjectId,
    weekday: 1,
    start: "09:00",
    end: "10:30",
    effectiveFrom: todayIso(),
    effectiveUntil: defaultUntil(),
    weekParity: "all",
    location: "",
    category: "",
  };
}

export default function RulesPanel() {
  const subjects = useQuery(api.subjects.list, { includeArchived: true });
  const rules = useQuery(api.schedule.listRules, {});
  const conflicts = useQuery(api.schedule.ruleConflicts, {});
  const [editing, setEditing] = useState<Id<"scheduleRules"> | null>(null);
  const [creating, setCreating] = useState(false);

  const subjectsById = useMemo(() => {
    const map = new Map<Id<"subjects">, Doc<"subjects">>();
    for (const subject of subjects ?? []) {
      map.set(subject._id, subject);
    }
    return map;
  }, [subjects]);

  const conflicted = useMemo(() => {
    const ids = new Set<Id<"scheduleRules">>();
    for (const pair of conflicts ?? []) {
      ids.add(pair.a);
      ids.add(pair.b);
    }
    return ids;
  }, [conflicts]);

  if (subjects === undefined || rules === undefined) {
    return <p className="text-sm text-neutral-500">Loading…</p>;
  }

  const selectable = subjects.filter((subject) => !subject.archived);

  if (selectable.length === 0) {
    return (
      <p className={`${cardClass} text-sm text-neutral-500`}>
        Add a subject first - a schedule rule needs one.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
        <p className="text-sm text-neutral-500">
          Times are wall-clock in the timezone set in Settings, so a rule keeps
          its local time across a DST change.
        </p>
        {!creating && (
          <motion.button
            type="button"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            className={`${secondaryButtonClass} self-start sm:self-auto shrink-0`}
            onClick={() => setCreating(true)}
          >
            New rule
          </motion.button>
        )}
      </div>

      <AnimatePresence>
        {creating && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <RuleForm
              subjects={selectable}
              draft={emptyDraft(selectable[0]._id)}
              onDone={() => setCreating(false)}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {rules.length === 0 && !creating && (
        <p className={`${cardClass} text-sm text-neutral-500`}>
          No schedule rules yet.
        </p>
      )}

      {WEEKDAY_NAMES.map((dayName, weekday) => {
        const dayRules = rules.filter((rule) => rule.weekday === weekday);
        if (dayRules.length === 0) {
          return null;
        }
        return (
          <div key={dayName} className="flex flex-col gap-2">
            <h3 className="text-xs font-medium uppercase tracking-wide text-neutral-500">
              {dayName}
            </h3>
            <AnimatePresence initial={false}>
              {dayRules.map((rule) =>
                editing === rule._id ? (
                  <motion.div
                    key={rule._id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <RuleForm
                      rule={rule}
                      // An archived subject stays selectable for its own rules, so
                      // the rule remains editable after the subject is retired.
                      subjects={
                        selectable.some((s) => s._id === rule.subjectId)
                          ? selectable
                          : [...selectable, subjectsById.get(rule.subjectId)].filter(
                              (s): s is Doc<"subjects"> => s !== undefined,
                            )
                      }
                      draft={{
                        subjectId: rule.subjectId,
                        weekday: rule.weekday,
                        start: formatMinute(rule.startMinute),
                        end: formatMinute(rule.endMinute),
                        effectiveFrom: rule.effectiveFrom,
                        effectiveUntil: rule.effectiveUntil,
                        weekParity: rule.weekParity,
                        location: rule.location ?? "",
                        category: rule.category ?? "",
                      }}
                      onDone={() => setEditing(null)}
                    />
                  </motion.div>
                ) : (
                  <motion.div
                    key={rule._id}
                    layout
                    initial={{ opacity: 0, y: 3 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.98 }}
                  >
                    <RuleRow
                      rule={rule}
                      subject={subjectsById.get(rule.subjectId)}
                      conflicted={conflicted.has(rule._id)}
                      onEdit={() => setEditing(rule._id)}
                    />
                  </motion.div>
                ),
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}

function RuleRow({
  rule,
  subject,
  conflicted,
  onEdit,
}: {
  rule: Doc<"scheduleRules">;
  subject: Doc<"subjects"> | undefined;
  conflicted: boolean;
  onEdit: () => void;
}) {
  const update = useMutation(api.schedule.updateRule);
  const remove = useMutation(api.schedule.removeRule);
  const [error, setError] = useState<string | null>(null);

  async function run(action: () => Promise<unknown>, fallback: string) {
    setError(null);
    try {
      await action();
    } catch (caught) {
      setError(errorMessage(caught, fallback));
    }
  }

  return (
    <div className={`${cardClass} flex flex-wrap items-center gap-3`}>
      <div className="flex items-center gap-2 min-w-0">
        <span
          aria-hidden
          className="h-3 w-3 shrink-0 rounded-full"
          style={{ backgroundColor: subject?.color ?? "#9CA3AF" }}
        />
        <span className={rule.active ? "font-medium truncate" : "text-neutral-500 truncate"}>
          {subject?.name ?? "Unknown subject"}
        </span>
      </div>
      <span className="font-mono text-sm text-neutral-600 dark:text-neutral-400">
        {formatMinute(rule.startMinute)}–{formatMinute(rule.endMinute)}
      </span>
      {rule.weekParity !== "all" && (
        <span className="rounded bg-neutral-100 px-1.5 py-0.5 text-xs text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400">
          {PARITY_LABEL[rule.weekParity]}
        </span>
      )}
      {rule.category !== undefined && (
        <span className="rounded bg-neutral-100 px-1.5 py-0.5 text-xs text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400">
          {rule.category}
        </span>
      )}
      {rule.location !== undefined && (
        <span className="text-xs text-neutral-500">{rule.location}</span>
      )}
      <span className="text-xs text-neutral-500">
        {rule.effectiveFrom} → {rule.effectiveUntil}
      </span>
      {!rule.active && <span className="text-xs text-neutral-500">paused</span>}
      {conflicted && (
        <span
          className="text-xs text-amber-600 dark:text-amber-400"
          title="Another active rule covers the same time on this weekday"
        >
          ⚠ overlaps another rule
        </span>
      )}

      <div className="w-full sm:w-auto sm:ml-auto flex items-center justify-end gap-2 pt-2 sm:pt-0 border-t sm:border-t-0 border-neutral-100 dark:border-neutral-800/80">
        <button type="button" className={secondaryButtonClass} onClick={onEdit}>
          Edit
        </button>
        <button
          type="button"
          className={secondaryButtonClass}
          onClick={() =>
            void run(
              () => update({ id: rule._id, active: !rule.active }),
              "Could not change the rule.",
            )
          }
        >
          {rule.active ? "Pause" : "Resume"}
        </button>
        <button
          type="button"
          className={secondaryButtonClass}
          onClick={() =>
            void run(
              () => remove({ id: rule._id }),
              "Could not delete the rule.",
            )
          }
        >
          Delete
        </button>
      </div>

      {error !== null && (
        <p className="w-full text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
    </div>
  );
}

function RuleForm({
  rule,
  subjects,
  draft,
  onDone,
}: {
  rule?: Doc<"scheduleRules">;
  subjects: Doc<"subjects">[];
  draft: Draft;
  onDone: () => void;
}) {
  const create = useMutation(api.schedule.createRule);
  const update = useMutation(api.schedule.updateRule);
  const [form, setForm] = useState<Draft>(draft);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  function set<K extends keyof Draft>(key: K, value: Draft[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);

    const startMinute = parseMinute(form.start);
    const endMinute = parseMinute(form.end);
    if (startMinute === null || endMinute === null) {
      setError("Times must be in HH:MM form.");
      return;
    }
    if (endMinute <= startMinute) {
      setError("The end time must be after the start time.");
      return;
    }

    setSaving(true);
    try {
      const fields = {
        subjectId: form.subjectId as Id<"subjects">,
        weekday: form.weekday,
        startMinute,
        endMinute,
        effectiveFrom: form.effectiveFrom,
        effectiveUntil: form.effectiveUntil,
        weekParity: form.weekParity,
        location: form.location,
        category: form.category,
      };
      if (rule === undefined) {
        await create(fields);
      } else {
        await update({ id: rule._id, ...fields });
      }
      onDone();
    } catch (caught) {
      setError(errorMessage(caught, "Could not save the rule."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className={`${cardClass} flex flex-col gap-3`}>
      <div className="flex flex-wrap gap-3">
        <label className="flex w-full sm:min-w-56 sm:flex-1 flex-col gap-1 text-sm">
          <span className="text-neutral-600 dark:text-neutral-400">Subject</span>
          <select
            className={inputClass}
            value={form.subjectId}
            onChange={(e) => set("subjectId", e.target.value)}
          >
            {subjects.map((subject) => (
              <option key={subject._id} value={subject._id}>
                {subject.name}
              </option>
            ))}
          </select>
        </label>

        <label className="flex w-full sm:w-40 flex-col gap-1 text-sm">
          <span className="text-neutral-600 dark:text-neutral-400">Weekday</span>
          <select
            className={inputClass}
            value={form.weekday}
            onChange={(e) => set("weekday", Number(e.target.value))}
          >
            {WEEKDAY_NAMES.map((name, index) => (
              <option key={name} value={index}>
                {name}
              </option>
            ))}
          </select>
        </label>

        <div className="flex w-full sm:w-auto gap-3">
          <label className="flex flex-1 sm:w-28 flex-col gap-1 text-sm">
            <span className="text-neutral-600 dark:text-neutral-400">Start</span>
            <input
              className={inputClass}
              type="time"
              value={form.start}
              onChange={(e) => set("start", e.target.value)}
            />
          </label>

          <label className="flex flex-1 sm:w-28 flex-col gap-1 text-sm">
            <span className="text-neutral-600 dark:text-neutral-400">End</span>
            <input
              className={inputClass}
              type="time"
              value={form.end}
              onChange={(e) => set("end", e.target.value)}
            />
          </label>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <label className="flex w-full sm:w-44 flex-col gap-1 text-sm">
          <span className="text-neutral-600 dark:text-neutral-400">
            Effective from
          </span>
          <input
            className={inputClass}
            type="date"
            value={form.effectiveFrom}
            onChange={(e) => set("effectiveFrom", e.target.value)}
          />
        </label>

        <label className="flex w-full sm:w-44 flex-col gap-1 text-sm">
          <span className="text-neutral-600 dark:text-neutral-400">
            Effective until
          </span>
          <input
            className={inputClass}
            type="date"
            value={form.effectiveUntil}
            onChange={(e) => set("effectiveUntil", e.target.value)}
          />
        </label>

        <label className="flex w-full sm:w-40 flex-col gap-1 text-sm">
          <span className="text-neutral-600 dark:text-neutral-400">Weeks</span>
          <select
            className={inputClass}
            value={form.weekParity}
            onChange={(e) => set("weekParity", e.target.value as Parity)}
          >
            {(Object.keys(PARITY_LABEL) as Parity[]).map((parity) => (
              <option key={parity} value={parity}>
                {PARITY_LABEL[parity]}
              </option>
            ))}
          </select>
        </label>

        <label className="flex w-full sm:min-w-48 sm:flex-1 flex-col gap-1 text-sm">
          <span className="text-neutral-600 dark:text-neutral-400">Location</span>
          <input
            className={inputClass}
            value={form.location}
            onChange={(e) => set("location", e.target.value)}
            placeholder="optional"
          />
        </label>

        <label className="flex w-full sm:min-w-40 sm:flex-1 flex-col gap-1 text-sm">
          <span className="text-neutral-600 dark:text-neutral-400">Category</span>
          <input
            className={inputClass}
            value={form.category}
            onChange={(e) => set("category", e.target.value)}
            placeholder="Lecture / Tutorial"
          />
        </label>
      </div>

      {form.weekParity !== "all" && (
        <p className="text-xs text-neutral-500">
          Parity follows the ISO week number, so “odd” means ISO weeks 1, 3, 5…
        </p>
      )}

      {error !== null && (
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      )}

      <div className="flex items-center gap-2">
        <button type="submit" className={primaryButtonClass} disabled={saving}>
          {rule === undefined ? "Add rule" : "Save"}
        </button>
        <button type="button" className={secondaryButtonClass} onClick={onDone}>
          Cancel
        </button>
      </div>
    </form>
  );
}
